import { useMemo, useState } from 'react';
import { Save } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { useSetContacts, useUpsertKyc } from '@/hooks/api/usePlatform';
import { useUnsavedGuard } from '@/hooks/useUnsavedGuard';
import { extractErrorMessage } from '@/lib/error';
import { showToast } from '@/lib/toast';
import type {
  PcContact,
  PcKyc,
  ContactRole,
  ContactInput,
} from '@/services/api/property-content.api';
import { CONTACT_ROLES } from '@/services/api/property-content.api';
import { SectionCard, TextField, fieldInputClass, InternalBadge } from './fields';

interface ContactFormRow {
  name: string;
  email: string;
  phone: string;
}

function buildContactForm(contacts: PcContact[]): Record<ContactRole, ContactFormRow> {
  const byRole = new Map(contacts.map((c) => [c.role, c]));
  const out = {} as Record<ContactRole, ContactFormRow>;
  for (const { value } of CONTACT_ROLES) {
    const c = byRole.get(value);
    out[value] = { name: c?.name ?? '', email: c?.email ?? '', phone: c?.phone ?? '' };
  }
  return out;
}

interface KycForm {
  registeredCompany: string;
  addressLine1: string;
  city: string;
  postcode: string;
  country: string;
  vatId: string;
}

function buildKycForm(kyc: PcKyc | null): KycForm {
  return {
    registeredCompany: kyc?.registeredCompany ?? '',
    addressLine1: kyc?.addressLine1 ?? '',
    city: kyc?.city ?? '',
    postcode: kyc?.postcode ?? '',
    country: kyc?.country ?? '',
    vatId: kyc?.vatId ?? '',
  };
}

export function ContactsKycTab({
  propertyId,
  contacts,
  kyc,
}: {
  propertyId: string;
  contacts: PcContact[];
  kyc: PcKyc | null;
}) {
  const initialContacts = useMemo(() => buildContactForm(contacts), [contacts]);
  const initialKyc = useMemo(() => buildKycForm(kyc), [kyc]);
  const [contactForm, setContactForm] = useState(initialContacts);
  const [kycForm, setKycForm] = useState(initialKyc);
  const [error, setError] = useState('');

  const contactsMutation = useSetContacts(propertyId);
  const kycMutation = useUpsertKyc(propertyId);

  const isDirty = useMemo(
    () =>
      JSON.stringify(contactForm) !== JSON.stringify(initialContacts) ||
      JSON.stringify(kycForm) !== JSON.stringify(initialKyc),
    [contactForm, initialContacts, kycForm, initialKyc],
  );
  useUnsavedGuard(isDirty);

  function updateContact(role: ContactRole, patch: Partial<ContactFormRow>) {
    setContactForm((prev) => ({ ...prev, [role]: { ...prev[role], ...patch } }));
  }
  const setKyc = <K extends keyof KycForm>(key: K, value: KycForm[K]) =>
    setKycForm((f) => ({ ...f, [key]: value }));

  async function handleSave() {
    setError('');
    // Only persist contact rows that carry at least one value.
    const contactPayload: ContactInput[] = CONTACT_ROLES.map(({ value }) => {
      const row = contactForm[value];
      return { role: value, row };
    })
      .filter(({ row }) => row.name.trim() || row.email.trim() || row.phone.trim())
      .map(({ role, row }) => ({
        role,
        ...(row.name.trim() ? { name: row.name.trim() } : {}),
        ...(row.email.trim() ? { email: row.email.trim() } : {}),
        ...(row.phone.trim() ? { phone: row.phone.trim() } : {}),
      }));

    try {
      await Promise.all([
        contactsMutation.mutateAsync(contactPayload),
        kycMutation.mutateAsync({
          registeredCompany: kycForm.registeredCompany.trim(),
          addressLine1: kycForm.addressLine1.trim(),
          city: kycForm.city.trim(),
          postcode: kycForm.postcode.trim(),
          country: kycForm.country.trim(),
          vatId: kycForm.vatId.trim(),
        }),
      ]);
      showToast('Contacts & KYC saved');
    } catch (err) {
      setError(extractErrorMessage(err));
      showToast('Could not save contacts & KYC', 'error');
    }
  }

  const pending = contactsMutation.isPending || kycMutation.isPending;

  return (
    <div className="space-y-6">
      {error && (
        <div className="rounded-lg bg-red-50 border border-red-200 p-3 text-sm text-red-700" role="alert">
          {error}
        </div>
      )}

      <SectionCard title="Contact persons" internal description="Operational staff routing — never shown on the storefront.">
        <div className="space-y-4">
          {CONTACT_ROLES.map(({ value, label }) => {
            const row = contactForm[value];
            return (
              <div key={value} className="rounded-lg border border-gray-100 p-4">
                <h4 className="mb-3 text-sm font-semibold text-gray-700">{label}</h4>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <input aria-label={`${label} name`} className={fieldInputClass} placeholder="Name" maxLength={255} value={row.name} onChange={(e) => updateContact(value, { name: e.target.value })} />
                  <input aria-label={`${label} email`} type="email" className={fieldInputClass} placeholder="Email" maxLength={255} value={row.email} onChange={(e) => updateContact(value, { email: e.target.value })} />
                  <input aria-label={`${label} phone`} className={fieldInputClass} placeholder="Phone" maxLength={50} value={row.phone} onChange={(e) => updateContact(value, { phone: e.target.value })} />
                </div>
              </div>
            );
          })}
        </div>
      </SectionCard>

      <SectionCard title="KYC / VAT" internal description="Legal / financial entity details — fenced, never public.">
        <TextField id="kyc-company" label="Registered company" value={kycForm.registeredCompany} onChange={(v) => setKyc('registeredCompany', v)} maxLength={255} internal />
        <TextField id="kyc-address" label="Address line 1" value={kycForm.addressLine1} onChange={(v) => setKyc('addressLine1', v)} maxLength={500} internal />
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <TextField id="kyc-city" label="City" value={kycForm.city} onChange={(v) => setKyc('city', v)} maxLength={100} internal />
          <TextField id="kyc-postcode" label="Postcode" value={kycForm.postcode} onChange={(v) => setKyc('postcode', v)} maxLength={20} internal />
          <TextField id="kyc-country" label="Country / region" value={kycForm.country} onChange={(v) => setKyc('country', v)} maxLength={100} internal />
        </div>
        <TextField id="kyc-vat" label="VAT ID" value={kycForm.vatId} onChange={(v) => setKyc('vatId', v)} maxLength={64} internal className="max-w-sm" />
      </SectionCard>

      <div className="sticky bottom-0 flex items-center justify-between gap-3 border-t border-gray-200 bg-white/90 py-3 backdrop-blur">
        <span className="flex items-center gap-2 text-sm text-gray-500" aria-live="polite">
          <InternalBadge />
          {isDirty ? 'You have unsaved changes.' : 'All changes saved.'}
        </span>
        <Button onClick={handleSave} isLoading={pending} disabled={!isDirty || pending}>
          <Save size={16} aria-hidden="true" />
          Save contacts &amp; KYC
        </Button>
      </div>
    </div>
  );
}
